import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../lib/axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Search, MoreVertical, Zap, User, TrendingUp,
  BarChart3, Loader2, Plus, AlertCircle, Edit2, Trash2, X,
  ArrowDownAZ, CalendarDays, Percent, Clock, Archive, Palette
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '../lib/utils';
import { useAuthStore } from '../store/useAuthStore';
import { useModules } from '../lib/useModules';
import ContactModal from '../components/contacts/ContactModal';

/* ─────────────────────────────────────────────────────────
 * StrictModeDroppable
 * ───────────────────────────────────────────────────────── */
function StrictModeDroppable({ children, ...props }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  if (!ready) return null;
  return <Droppable {...props}>{children}</Droppable>;
}

function scoreClass(score) {
  if (score >= 80) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (score >= 50) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-rose-600 bg-rose-50 border-rose-200';
}

export default function LeadsBoard() {
  const { user } = useAuthStore();
  const { isFeatureEnabled } = useModules();

  const canSeeArchived = isFeatureEnabled('leads', 'archived');
  const canUseAutopilot = isFeatureEnabled('leads', 'autopilot');
  const canManageStages = isFeatureEnabled('leads', 'manage_stages');
  // Crear un lead usa el endpoint de contactos, así que requiere ambas.
  const canCreateLead = isFeatureEnabled('leads', 'manual_create') && isFeatureEnabled('contacts', 'create');
  const [columnBorderColors, setColumnBorderColors] = useState({});
  const [leadsById, setLeadsById] = useState({});
  const [stages, setStages] = useState([]);
  const [cols, setCols] = useState({});
  const colsRef = useRef(cols);

  useEffect(() => {
    if (user?.id) {
      try {
        const stored = localStorage.getItem(`tornado_kanban_borders_${user.id}`);
        if (stored) {
          setColumnBorderColors(JSON.parse(stored));
        }
      } catch (e) {
        console.error('Error loading column border colors', e);
      }
    }
  }, [user]);

  const handleColorChange = (stageId, color) => {
    const newColors = { ...columnBorderColors };
    if (color) {
      newColors[stageId] = color;
    } else {
      delete newColors[stageId];
    }
    setColumnBorderColors(newColors);
    if (user?.id) {
      localStorage.setItem(`tornado_kanban_borders_${user.id}`, JSON.stringify(newColors));
    }
  };

  useEffect(() => { colsRef.current = cols; }, [cols]);

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [search, setSearch] = useState('');

  // Modals state
  const [editStage, setEditStage] = useState(null);
  const [deleteStage, setDeleteStage] = useState(null);
  const [isAddingStage, setIsAddingStage] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [columnSorts, setColumnSorts] = useState({}); // { stageSlug: sortType }
  const [stageFormData, setStageFormData] = useState({ name: '' });
  const [newLeadStage, setNewLeadStage] = useState(null); // stage.slug del modal "Nuevo Lead", o null si está cerrado

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/leads');

      const loadedStages = Array.isArray(data.stages) ? data.stages : (data.stages?.data || []);
      setStages(loadedStages);

      const byId = {};
      const newCols = Object.fromEntries((loadedStages || []).map(s => [s.slug, []]));

      Object.entries(data.leads ?? {}).forEach(([stageSlug, list]) => {
        (list ?? []).forEach(c => {
          byId[c.id] = c;
          if (newCols[stageSlug] !== undefined) {
            newCols[stageSlug].push(c.id);
          } else if (loadedStages.length > 0) {
            // Fallback to first stage
            newCols[loadedStages[0].slug].push(c.id);
          }
        });
      });

      setLeadsById(byId);
      setCols(newCols);
      setErrorMsg('');
    } catch (e) {
      console.error(e);
      setErrorMsg('No se pudo cargar el tablero.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const onDragEnd = async (result) => {
    const { source: src, destination: dst, draggableId } = result;
    if (!dst) return;
    if (src.droppableId === dst.droppableId && src.index === dst.index) return;

    const leadId = Number(draggableId);
    const fromStage = src.droppableId;
    const toStage = dst.droppableId;

    const snapshot = colsRef.current;
    const nextCols = Object.fromEntries((stages || []).map(s => [s.slug, [...(snapshot[s.slug] || [])]]));

    const fi = nextCols[fromStage].indexOf(leadId);
    if (fi === -1) return;
    nextCols[fromStage].splice(fi, 1);
    nextCols[toStage].splice(dst.index, 0, leadId);

    setCols(nextCols);

    if (fromStage === toStage) return;

    setSavingId(leadId);
    try {
      await axios.patch(`/api/leads/${leadId}/move-stage`, { stage: toStage });
      setLeadsById(prev => ({
        ...prev,
        [leadId]: { ...prev[leadId], pipeline_stage: toStage },
      }));
      setErrorMsg('');
    } catch (err) {
      const status = err?.response?.status ?? '?';
      const msg = err?.response?.data?.message ?? err.message;
      setErrorMsg(`Error al guardar (${status}: ${msg}). Operación revertida.`);
      await loadLeads();
    } finally {
      setSavingId(null);
    }
  };

  const toggleAutopilot = async (id) => {
    try {
      const { data } = await axios.post(`/api/leads/${id}/toggle-autopilot`);
      setLeadsById(prev => ({
        ...prev,
        [id]: { ...prev[id], is_autopilot_enabled: data.is_autopilot_enabled },
      }));
    } catch (e) {
      console.error(e);
    }
  };

  /* ── Stage Management ── */
  const handleSaveStage = async (e) => {
    e.preventDefault();
    if (!stageFormData.name.trim()) return;

    try {
      if (editStage) {
        await axios.put(`/api/pipeline-stages/${editStage.id}`, stageFormData);
      } else {
        await axios.post('/api/pipeline-stages', stageFormData);
      }
      setEditStage(null);
      setIsAddingStage(false);
      setStageFormData({ name: '' });
      await loadLeads();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al guardar etapa');
    }
  };

  const handleDeleteStage = async () => {
    if (!deleteStage) return;
    try {
      await axios.delete(`/api/pipeline-stages/${deleteStage.id}`);
      setDeleteStage(null);
      await loadLeads();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar etapa');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin text-primary" size={40} />
          <p className="font-semibold text-slate-400">Cargando tablero...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 md:p-8 gap-6 overflow-hidden relative" onClick={() => setMenuOpen(null)}>
      {/* ── Header ── */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 shrink-0">
        <div className="flex-1">
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white flex items-center gap-3">
            Board de Leads
            <span className="text-[10px] leading-none bg-primary/10 text-primary px-3 py-1.5 rounded-full border border-primary/20 uppercase tracking-[0.2em] font-black">
              AI Enabled
            </span>
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Arrastra las fichas entre las columnas para avanzar prospectos
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {errorMsg && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-400/10 text-red-600 dark:text-red-400 text-xs px-4 py-2.5 rounded-xl border border-red-100 dark:border-red-500/20 max-w-xs">
              <AlertCircle size={15} className="shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              type="text"
              placeholder="Buscar lead..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 w-52 shadow-sm text-slate-900 dark:text-white placeholder:text-slate-400"
            />
          </div>

          {canSeeArchived && (
            <Link
              to="/leads/archived"
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-600 dark:text-slate-300 rounded-xl transition-all font-bold text-sm border border-slate-200 dark:border-white/5"
            >
              <Archive size={16} />
              <span className="hidden sm:inline">Archivo</span>
            </Link>
          )}
        </div>
      </div>

      {/* ── Kanban ── */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex-1 overflow-x-auto overflow-y-hidden pb-6 custom-scrollbar">
          <div className="flex gap-4 h-full items-start" style={{ minWidth: `${(stages.length * 18) + 20}rem` }}>

            {(Array.isArray(stages) ? stages : []).map(stage => {
              const ids = cols[stage.slug] ?? [];
              const sortType = columnSorts[stage.slug] || 'date_newest';

              let visible = [...ids];
              if (search) {
                visible = visible.filter(id => leadsById[id]?.full_name?.toLowerCase().includes(search.toLowerCase()));
              }

              visible.sort((idA, idB) => {
                const a = leadsById[idA];
                const b = leadsById[idB];
                if (!a || !b) return 0;
                if (sortType === 'name') return (a.full_name || '').localeCompare(b.full_name || '');
                if (sortType === 'date_newest') return new Date(b.created_at) - new Date(a.created_at);
                if (sortType === 'date_oldest') return new Date(a.created_at) - new Date(b.created_at);
                if (sortType === 'score_desc') return (b.lead_score || 0) - (a.lead_score || 0);
                if (sortType === 'score_asc') return (a.lead_score || 0) - (b.lead_score || 0);
                return 0;
              });

              return (
                <div
                  key={stage.id}
                  className={`w-72 shrink-0 flex flex-col rounded-2xl bg-slate-100/70 dark:bg-white/[0.03] transition-all duration-300 ${
                    columnBorderColors[stage.id] ? 'border-2' : 'border border-slate-200/80 dark:border-white/5'
                  }`}
                  style={{
                    maxHeight: 'calc(100vh - 230px)',
                    borderColor: columnBorderColors[stage.id] || undefined,
                    boxShadow: columnBorderColors[stage.id] ? `0 4px 20px -2px ${columnBorderColors[stage.id]}20` : undefined
                  }}
                >
                  {/* Column header */}
                  <div className="px-4 py-3.5 flex items-center justify-between shrink-0 relative">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full shadow-[0_0_5px_rgba(214,61,0,0.6)]" style={{ backgroundColor: stage.color || '#3b82f6' }} />
                      <span className="text-xs font-black uppercase tracking-[0.12em] text-slate-700 dark:text-white/80 truncate max-w-[120px]" title={stage.name}>
                        {stage.name}
                      </span>
                      <span className="text-[10px] font-bold bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded-md">
                        {ids.length}
                      </span>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === stage.id ? null : stage.id); }}
                      className="p-1 hover:bg-slate-200 dark:hover:bg-white/10 rounded-md transition-colors"
                    >
                      <MoreVertical size={14} className="text-slate-400" />
                    </button>

                    {menuOpen === stage.id && (
                      <div className="absolute top-10 right-2 w-48 bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 rounded-xl shadow-lg overflow-hidden z-20">
                        <div className="px-4 py-2 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ordenar por</span>
                        </div>

                        <button
                          onClick={(e) => { e.stopPropagation(); setColumnSorts(p => ({ ...p, [stage.slug]: 'name' })); setMenuOpen(null); }}
                          className={`w-full text-left px-4 py-2 text-[11px] font-semibold hover:bg-slate-50 dark:hover:bg-white/5 flex items-center justify-between ${columnSorts[stage.slug] === 'name' ? 'text-primary' : ''}`}
                        >
                          <div className="flex items-center gap-2"><ArrowDownAZ size={12} /> Nombres</div>
                          {columnSorts[stage.slug] === 'name' && <div className="w-1 h-1 rounded-full bg-primary" />}
                        </button>

                        <button
                          onClick={(e) => { e.stopPropagation(); setColumnSorts(p => ({ ...p, [stage.slug]: 'date_newest' })); setMenuOpen(null); }}
                          className={`w-full text-left px-4 py-2 text-[11px] font-semibold hover:bg-slate-50 dark:hover:bg-white/5 flex items-center justify-between ${(!columnSorts[stage.slug] || columnSorts[stage.slug] === 'date_newest') ? 'text-primary' : ''}`}
                        >
                          <div className="flex items-center gap-2"><CalendarDays size={12} /> Más recientes primero</div>
                          {(!columnSorts[stage.slug] || columnSorts[stage.slug] === 'date_newest') && <div className="w-1 h-1 rounded-full bg-primary" />}
                        </button>

                        <button
                          onClick={(e) => { e.stopPropagation(); setColumnSorts(p => ({ ...p, [stage.slug]: 'date_oldest' })); setMenuOpen(null); }}
                          className={`w-full text-left px-4 py-2 text-[11px] font-semibold hover:bg-slate-50 dark:hover:bg-white/5 flex items-center justify-between ${columnSorts[stage.slug] === 'date_oldest' ? 'text-primary' : ''}`}
                        >
                          <div className="flex items-center gap-2"><Clock size={12} /> Más recientes al final</div>
                          {columnSorts[stage.slug] === 'date_oldest' && <div className="w-1 h-1 rounded-full bg-primary" />}
                        </button>

                        <button
                          onClick={(e) => { e.stopPropagation(); setColumnSorts(p => ({ ...p, [stage.slug]: 'score_desc' })); setMenuOpen(null); }}
                          className={`w-full text-left px-4 py-2 text-[11px] font-semibold hover:bg-slate-50 dark:hover:bg-white/5 flex items-center justify-between ${columnSorts[stage.slug] === 'score_desc' ? 'text-primary' : ''}`}
                        >
                          <div className="flex items-center gap-2"><Percent size={12} /> Calificación más alta</div>
                          {columnSorts[stage.slug] === 'score_desc' && <div className="w-1 h-1 rounded-full bg-primary" />}
                        </button>

                        <button
                          onClick={(e) => { e.stopPropagation(); setColumnSorts(p => ({ ...p, [stage.slug]: 'score_asc' })); setMenuOpen(null); }}
                          className={`w-full text-left px-4 py-2 text-[11px] font-semibold hover:bg-slate-50 dark:hover:bg-white/5 flex items-center justify-between ${columnSorts[stage.slug] === 'score_asc' ? 'text-primary' : ''}`}
                        >
                          <div className="flex items-center gap-2"><Percent size={12} className="rotate-180" /> Calificación más baja</div>
                          {columnSorts[stage.slug] === 'score_asc' && <div className="w-1 h-1 rounded-full bg-primary" />}
                        </button>

                        {canManageStages && (
                          <>
                            <div className="h-px bg-slate-100 dark:bg-white/5 my-1" />

                            <button
                              onClick={(e) => { e.stopPropagation(); setEditStage(stage); setStageFormData({ name: stage.name }); setMenuOpen(null); }}
                              className="w-full text-left px-4 py-2 text-[11px] font-semibold hover:bg-slate-50 dark:hover:bg-white/5 flex items-center gap-2"
                            >
                              <Edit2 size={12} /> Editar Etapa
                            </button>
                            {!stage.is_system && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteStage(stage); setMenuOpen(null); }}
                                className="w-full text-left px-4 py-2 text-[11px] font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center gap-2"
                              >
                                <Trash2 size={12} /> Eliminar Etapa
                              </button>
                            )}
                          </>
                        )}

                        <div className="h-px bg-slate-100 dark:bg-white/5 my-1" />

                        <div className="px-4 py-1.5 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-white/[0.02]">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                            <Palette size={10} /> Color del Borde
                          </span>
                        </div>

                        <div className="px-4 py-2 flex flex-wrap gap-2 justify-start items-center">
                          {['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'].map((c) => (
                            <button
                              key={c}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleColorChange(stage.id, c);
                              }}
                              className="w-4 h-4 rounded-full border border-slate-200 dark:border-white/20 transition-transform hover:scale-125 focus:outline-none"
                              style={{ backgroundColor: c }}
                              title={c}
                            />
                          ))}
                          
                          <div className="relative w-4 h-4 rounded-full border border-slate-200 dark:border-white/20 hover:scale-125 transition-transform overflow-hidden cursor-pointer">
                            <input
                              type="color"
                              value={columnBorderColors[stage.id] || '#cbd5e1'}
                              onChange={(e) => {
                                handleColorChange(stage.id, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute inset-0 w-full h-full p-0 border-0 cursor-pointer opacity-0"
                              title="Color personalizado"
                            />
                            <div className="w-full h-full bg-gradient-to-tr from-red-500 via-green-500 to-blue-500" />
                          </div>
                        </div>

                        {columnBorderColors[stage.id] && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleColorChange(stage.id, '');
                              setMenuOpen(null);
                            }}
                            className="w-full text-left px-4 py-1.5 text-[10px] font-semibold text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5 flex items-center gap-1.5"
                          >
                            <X size={10} /> Restablecer color
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Droppable column body */}
                  <StrictModeDroppable droppableId={stage.slug}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3 space-y-3 no-scrollbar transition-colors duration-150 ${snapshot.isDraggingOver ? 'bg-primary/5' : ''
                          }`}
                        style={{ minHeight: 160 }}
                      >
                        {visible.map((id, index) => {
                          const lead = leadsById[id];
                          if (!lead) return null;

                          return (
                            <Draggable key={id} draggableId={String(id)} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={provided.draggableProps.style}
                                  className={`relative bg-white dark:bg-surface-dark rounded-xl border select-none transition-shadow ${snapshot.isDragging
                                    ? 'border-primary/40 shadow-2xl ring-2 ring-primary/20 rotate-1 cursor-grabbing'
                                    : 'border-slate-100 dark:border-white/5 shadow-sm hover:shadow-md cursor-grab'
                                    }`}
                                >
                                  {lead.is_autopilot_enabled && (
                                    <div className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-amber-400" />
                                  )}

                                  {savingId === id && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 dark:bg-black/50 backdrop-blur-[2px]">
                                      <Loader2 className="animate-spin text-primary" size={20} />
                                    </div>
                                  )}

                                  <div className="p-4">
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                      <div className="min-w-0">
                                        <Link
                                          to={`/contacts/${lead.id}/360`}
                                          className="text-sm font-bold text-slate-900 dark:text-white hover:text-primary transition-colors block truncate"
                                        >
                                          {lead.full_name}
                                        </Link>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                          <p className="text-[11px] text-slate-400 truncate uppercase tracking-tight">
                                            {lead.company || '—'}
                                          </p>
                                          {lead.ai_summary && (
                                            <span className="flex items-center gap-0.5 text-[9px] bg-indigo-50 dark:bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-tighter border border-indigo-100 dark:border-indigo-500/20">
                                              <Zap size={8} />
                                              IA
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      {canUseAutopilot && (
                                        <button
                                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAutopilot(lead.id); }}
                                          className={`p-2 rounded-lg shrink-0 transition-all ${lead.is_autopilot_enabled
                                            ? 'bg-amber-50 text-amber-500 border border-amber-200'
                                            : 'bg-slate-50 dark:bg-white/5 text-slate-300 hover:text-amber-400'
                                            }`}
                                        >
                                          <Zap size={14} fill={lead.is_autopilot_enabled ? 'currentColor' : 'none'} />
                                        </button>
                                      )}
                                    </div>

                                    {lead.ai_summary && (
                                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 mb-3 bg-slate-50 dark:bg-white/5 p-2 rounded-lg italic border-l-2 border-indigo-400 line-clamp-2">
                                        "{lead.ai_summary}"
                                      </p>
                                    )}

                                    <div className="flex items-center justify-between pt-3 border-t border-slate-50 dark:border-white/5">
                                      <div className={`text-[10px] font-black px-2 py-1 rounded-lg border flex items-center gap-1 uppercase tracking-wide ${scoreClass(lead.lead_score)}`}>
                                        <BarChart3 size={10} />
                                        {lead.lead_score ?? 0}%
                                      </div>
                                      <div
                                        className="w-6 h-6 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center overflow-hidden"
                                        title={lead.owner?.name ?? 'Sin asignar'}
                                      >
                                        {lead.owner?.avatar
                                          ? <img src={resolveMediaUrl(lead.owner.avatar)} alt="" className="w-full h-full object-cover" />
                                          : <User size={11} className="text-slate-400" />
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </StrictModeDroppable>

                  {canCreateLead && (
                    <button
                      onClick={() => setNewLeadStage(stage.slug)}
                      className="mx-3 mb-3 py-2 rounded-xl border border-dashed border-slate-300 dark:border-white/10 text-[11px] font-bold text-slate-400 hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-all flex items-center justify-center gap-1.5 uppercase tracking-widest group shrink-0"
                    >
                      <Plus size={12} className="group-hover:rotate-90 transition-transform" />
                      Nuevo Lead
                    </button>
                  )}
                </div>
              );
            })}

            {/* Add Stage Column */}
            {canManageStages && (
              <div
                onClick={() => { setIsAddingStage(true); setStageFormData({ name: '' }); }}
                className="w-64 shrink-0 border-2 border-dashed border-slate-200 dark:border-white/10 rounded-2xl flex flex-col items-center justify-center gap-3 p-6 text-slate-400 hover:text-primary hover:border-primary/30 hover:bg-primary/5 transition-all cursor-pointer min-h-[200px] group"
              >
                <TrendingUp size={26} className="opacity-30 group-hover:opacity-100 transition-opacity" />
                <span className="text-[11px] font-black uppercase tracking-[0.15em] text-center">Añadir Etapa</span>
              </div>
            )}
          </div>
        </div>
      </DragDropContext>

      {/* Edit/Add Stage Modal */}
      {(editStage || isAddingStage) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
              <h3 className="font-bold text-lg">{isAddingStage ? 'Añadir Etapa' : 'Editar Etapa'}</h3>
              <button onClick={() => { setEditStage(null); setIsAddingStage(false); }} className="p-1 hover:bg-slate-100 dark:hover:bg-white/10 rounded-md text-slate-400">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSaveStage} className="p-6">
              <div className="mb-4">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Nombre de la Etapa</label>
                <input
                  type="text"
                  value={stageFormData.name}
                  onChange={e => setStageFormData({ ...stageFormData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 outline-none"
                  placeholder="Ej. Cita Agendada"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setEditStage(null); setIsAddingStage(false); }}
                  className="px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-bold bg-primary text-white rounded-xl shadow-lg shadow-primary/30 hover:bg-primary/90"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Stage Dialog */}
      {deleteStage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={24} />
            </div>
            <h3 className="font-bold text-lg mb-2">¿Eliminar etapa?</h3>
            <p className="text-sm text-slate-500 mb-6">
              Estás a punto de eliminar la etapa <strong>{deleteStage.name}</strong>.
              Cualquier lead en esta etapa será movido a la primera etapa disponible.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteStage(null)}
                className="px-6 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteStage}
                className="px-6 py-2 text-sm font-bold bg-red-500 text-white rounded-xl shadow-lg shadow-red-500/30 hover:bg-red-600"
              >
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nuevo Lead Modal — se abre desde el botón "+" de cada columna, con la etapa pre-cargada */}
      <ContactModal
        isOpen={!!newLeadStage}
        onClose={() => setNewLeadStage(null)}
        defaultPipelineStage={newLeadStage}
        onCreated={loadLeads}
      />

    </div>
  );
}
